import React from 'react';

/**
 * ResponsiveTable Component
 * 
 * Displays data in a table format on desktop and automatically converts
 * to a card-based layout on mobile devices for better readability.
 * 
 * Props:
 * - columns: Array of column definitions { key, label, render?, className? }
 * - data: Array of data objects
 * - keyExtractor: Function to extract unique key from data item
 * - emptyMessage: Message to display when no data
 * - cardClassName: Additional classes for mobile cards
 * - mobileRenderCard: Custom render function for mobile cards (optional)
 */
function ResponsiveTable({ 
  columns = [], 
  data = [], 
  keyExtractor = (item, index) => index,
  emptyMessage = "No data available",
  cardClassName = "",
  mobileRenderCard = null,
  tableClassName = ""
}) {
  
  // Default mobile card renderer
  const defaultMobileCardRender = (item, index) => (
    <div 
      key={keyExtractor(item, index)}
      className={`bg-ice-white rounded-xl shadow-brand border border-pale-dawn/50 p-4 space-y-3 hover:shadow-brand-lg transition-shadow duration-200 ${cardClassName}`}
    >
      {columns.map((column, colIndex) => {
        const value = column.render 
          ? column.render(item, index) 
          : item[column.key];
        
        // Skip columns marked as hidden on mobile
        if (column.hideOnMobile) return null;
        
        return (
          <div key={colIndex} className="flex justify-between items-start gap-3">
            <span className="text-sm font-semibold text-grey-dusk/70 min-w-[100px]">
              {column.label}
            </span>
            <span className={`text-sm text-grey-dusk flex-1 text-right ${column.mobileClassName || ''}`}>
              {value}
            </span>
          </div>
        );
      })}
    </div>
  );

  // Check if data is empty
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12">
        <i className="fas fa-inbox text-4xl text-gray-300 mb-3"></i>
        <p className="text-grey-dusk/70">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className={`min-w-full divide-y divide-gray-200 ${tableClassName}`}>
          <thead className="bg-pale-dawn/30">
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={`px-6 py-4 text-left text-xs font-semibold text-grey-dusk uppercase tracking-wider ${column.headerClassName || ''}`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-ice-white divide-y divide-gray-200">
            {data.map((item, rowIndex) => (
              <tr 
                key={keyExtractor(item, rowIndex)}
                className="hover:bg-sunrise-red/5 transition-colors duration-150"
              >
                {columns.map((column, colIndex) => {
                  const value = column.render 
                    ? column.render(item, rowIndex) 
                    : item[column.key];
                  
                  return (
                    <td
                      key={colIndex}
                      className={`px-6 py-4 whitespace-nowrap text-sm text-grey-dusk ${column.className || ''}`}
                    >
                      {value}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {data.map((item, index) => 
          mobileRenderCard 
            ? mobileRenderCard(item, index)
            : defaultMobileCardRender(item, index)
        )}
      </div>
    </>
  );
}

export default ResponsiveTable;
